import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Chip, IconButton, Paper, Snackbar, Stack, Typography, alpha, useTheme } from "@mui/material";
import { Sell, PlayArrow, Stop, ListAlt, ReceiptLong, TableChart, Insights, Close } from "@mui/icons-material";
import { ICore } from "../../core/ICore";
import { IForm } from "../../core/form/IForm";
import { useSaleEngineFormConfig } from "./useSaleEngineFormConfig";
import { useSocket } from "../../core/hooks/useSocket";
import { useDataFetch } from "../../core/hooks/useDataFetch";
import useCookie from "../../core/hooks/useCookie";
import { useResponsive } from "../../core/hooks/useResponsive";
import { Config } from "../../config/config";
import ProcessLogGrid from "./components/processLogGrid";
import OrderBookGrid from "./components/orderBookGrid";
import SaleHoldingsGrid from "./components/saleHoldingsGrid";
import { SaleSummary } from "./components/saleSummary";
import ReactiveConfig, { REACTIVE_ADVANCED_FIELDS } from "./components/ReactiveConfig";

const saleTypeOptions = [
  { key: "Fixed", value: "Fixed" },
  { key: "Reactive", value: "Reactive" },
];

const STATUS_CHIP = {
  Inprogress: { label: "Running", color: "success" },
  Completed: { label: "Completed", color: "info" },
  Failed: { label: "Failed", color: "error" },
  Stopped: { label: "Stopped", color: "warning" },
};

/** A titled panel inside the Live overview (same look as the Buy engine desk). */
const Panel = ({ icon: Icon, title, children, bodySx }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Paper elevation={0} sx={{ borderRadius: 2, p: { xs: 1, md: 1.5 }, border: `1px solid ${alpha(primary, 0.12)}`, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Icon sx={{ color: primary, fontSize: 22 }} />
        <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
      </Stack>
      <Box sx={bodySx}>{children}</Box>
    </Paper>
  );
};

/**
 * Sale engine desk - laid out like the Buy engine: Sale setup (type / exit levels / control), then the
 * Live overview (process log | order book, holdings, summary) fed over the WebSocket (staging
 * "saleProcess"). The run is per login, checks every minute and stops at the market close.
 */
const SaleEngine = () => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { saleEngineForm } = useSaleEngineFormConfig();
  const { connect, feedData } = useSocket("saleProcess");
  const { callAPI } = useDataFetch();
  const { getToken } = useCookie();
  const { isPhone } = useResponsive();
  const [hasProcessStarted, setHasProcessStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [autoOpenConfig, setAutoOpenConfig] = useState(false);
  const lastStatus = useRef(null);

  const isReactive = saleEngineForm.values.saleType === "Reactive";
  const processStatus = feedData?.status ?? null;
  const isRunning = processStatus === "Inprogress";
  const chip = STATUS_CHIP[processStatus] || { label: "Ready", color: "default" };
  const showOverview = hasProcessStarted && feedData;

  useEffect(() => {
    connect(getToken()?.profileID);
    // A run may still be going (or have just finished) from before this page was opened.
    const t = setTimeout(async () => {
      const response = await callAPI(Config.ITradeAPI.checkSaleProcess);
      if (response?.data?.status != null) setHasProcessStarted(true);
    }, 500);
    return () => clearTimeout(t);
  }, []);

  // When a run ends, say why (its last log line) - the chip alone is easy to miss.
  useEffect(() => {
    if (lastStatus.current === "Inprogress" && processStatus && processStatus !== "Inprogress" && processStatus !== "Stopped") {
      const last = feedData?.processLog?.[feedData.processLog.length - 1]?.log;
      setNotice({ severity: processStatus === "Failed" ? "error" : "info", message: last || `Sale process ${processStatus.toLowerCase()}.` });
    }
    lastStatus.current = processStatus;
  }, [processStatus, feedData]);

  const handleStart = async () => {
    const errors = await saleEngineForm.validateForm();
    if (Object.keys(errors).length > 0) {
      const fields = ["saleType", "stopProfit", "triggerProfit", "stopLoss", ...REACTIVE_ADVANCED_FIELDS];
      saleEngineForm.setTouched(Object.fromEntries(fields.map((f) => [f, true])));
      // An advanced field is wrong: open the popover so the error is seen.
      if (isReactive && REACTIVE_ADVANCED_FIELDS.some((f) => errors[f])) setAutoOpenConfig(true);
      return;
    }
    const v = saleEngineForm.values;
    setBusy(true);
    try {
      // Errors (market closed, already running, invalid levels) are shown by useDataFetch's alert.
      // Reactive: stopProfit = the trigger profit, plus the advanced settings.
      const advanced = isReactive ? Object.fromEntries(REACTIVE_ADVANCED_FIELDS.map((f) => [f, parseFloat(v[f])])) : {};
      const response = await callAPI(Config.ITradeAPI.startSaleProcess, {
        saleType: v.saleType,
        stopProfit: parseFloat(isReactive ? v.triggerProfit : v.stopProfit),
        stopLoss: parseFloat(v.stopLoss),
        ...advanced,
      });
      if (response?.status) setHasProcessStarted(true);
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    setBusy(true);
    try {
      const response = await callAPI(Config.ITradeAPI.stopSaleProcess);
      if (response?.data?.success) setNotice({ severity: "info", message: "Sale process stopped." });
    } finally {
      setBusy(false);
    }
  };

  const divider = `1px solid ${alpha(primary, 0.1)}`;
  const logBodySx = { height: { xs: 280, lg: 340 }, overflow: "auto" };

  return (
    <Box sx={{ minHeight: "100%", bgcolor: alpha(primary, 0.02), pb: 1.5, pt: { xs: 0.75, md: 1.25 }, px: { xs: 0.75, sm: 1.25, md: 1.5 } }}>
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          <Sell sx={{ fontSize: isPhone ? 22 : 28, color: primary }} />
          <Typography variant={isPhone ? "h6" : "h5"} fontWeight={800} letterSpacing="-0.02em" noWrap>Sale engine</Typography>
          <Chip size="small" label={chip.label} color={chip.color} variant={chip.color === "default" ? "outlined" : "filled"} />
          {isRunning && feedData?.saleType && <Chip size="small" variant="outlined" label={`${feedData.saleType} · scan ${feedData.scanCount ?? 0}`} />}
        </Stack>

        <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", border: `1px solid ${alpha(primary, 0.14)}`, boxShadow: `0 8px 24px ${alpha("#000", 0.04)}`, mb: 1.5 }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: alpha(primary, 0.06), borderBottom: divider }}>
            <Typography variant="overline" fontWeight={800} color="primary" sx={{ lineHeight: 1.2 }}>Sale setup</Typography>
          </Box>
          <IForm form={saleEngineForm}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr 1fr" } }}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 }, borderBottom: { xs: divider, md: "none" }, borderRight: { md: divider } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>Sale type</Typography>
                <ICore.IToggle model="saleType" label="Sale Type" options={saleTypeOptions} disabled={isRunning} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {isReactive
                    ? "Sells at stop loss until the trigger; then each new trigger locks a higher exit and sells when price falls to it."
                    : "Sells at stop profit or stop loss, fixed from each buy price."}
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.25, md: 1.5 }, borderBottom: { xs: divider, md: "none" }, borderRight: { md: divider } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>Exit levels (%)</Typography>
                <Box sx={{ display: "grid", gap: 1.25, alignItems: "start", gridTemplateColumns: isReactive ? "repeat(2, minmax(0, 1fr)) auto" : "repeat(2, minmax(0, 1fr))" }}>
                  {isReactive ? (
                    <ICore.ITextBox key="triggerProfit" model="triggerProfit" label="Trigger Profit" type="number" inputProps={{ step: "any", min: 0 }} disabled={isRunning} />
                  ) : (
                    <ICore.ITextBox key="stopProfit" model="stopProfit" label="Stop Profit" type="number" inputProps={{ step: "any", min: 0 }} disabled={isRunning} />
                  )}
                  <ICore.ITextBox model="stopLoss" label="Stop Loss" type="number" inputProps={{ step: "any", min: 0 }} disabled={isRunning} />
                  {isReactive && !isRunning && <ReactiveConfig autoOpen={autoOpenConfig} onAutoOpen={() => setAutoOpenConfig(false)} />}
                </Box>
              </Box>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>Control</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="contained" startIcon={<PlayArrow />} onClick={handleStart} disabled={isRunning || busy} sx={{ fontWeight: 700, flex: { xs: 1, sm: "0 0 auto" } }}>Start</Button>
                  <Button variant="outlined" startIcon={<Stop />} onClick={handleStop} disabled={!isRunning || busy} sx={{ fontWeight: 700, flex: { xs: 1, sm: "0 0 auto" } }}>Stop</Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Checks every minute during market hours and stops at the close.
                </Typography>
              </Box>
            </Box>
          </IForm>
        </Paper>

        {showOverview ? (
          <Paper elevation={0} sx={{ borderRadius: 2, p: { xs: 1, md: 1.5 }, border: `1px solid ${alpha(primary, 0.12)}`, boxShadow: `0 6px 20px ${alpha("#000", 0.03)}` }}>
            <Typography variant="overline" fontWeight={800} color="primary" sx={{ display: "block", mb: 1 }}>Live overview</Typography>
            <Box sx={{ display: "grid", gap: { xs: 1, md: 1.5 }, gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" } }}>
              <Panel icon={ListAlt} title="Process log" bodySx={logBodySx}>
                <ProcessLogGrid processLog={feedData?.processLog} />
              </Panel>
              <Panel icon={ReceiptLong} title="Order book" bodySx={logBodySx}>
                <OrderBookGrid orderBook={feedData?.orderBook} />
              </Panel>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Panel icon={TableChart} title="Holdings" bodySx={{ height: { xs: "auto", md: 360 }, maxHeight: { xs: 520, md: "none" }, overflow: "auto" }}>
                  <SaleHoldingsGrid holdings={feedData?.holdings} />
                </Panel>
              </Box>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Panel icon={Insights} title="Sale summary">
                  <SaleSummary data={feedData?.saleSummary} holdings={feedData?.holdings} />
                </Panel>
              </Box>
            </Box>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ p: 2, textAlign: "center", borderRadius: 2, border: `1px dashed ${alpha(primary, 0.25)}` }}>
            <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
              Start the sale engine to watch your holdings, orders and summary here.
            </Typography>
          </Paper>
        )}
      </Box>

      <Snackbar open={Boolean(notice)} autoHideDuration={notice?.severity === "error" ? 10000 : 6000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert
          severity={notice?.severity || "info"}
          variant="filled"
          action={<IconButton size="small" color="inherit" aria-label="Dismiss" onClick={() => setNotice(null)}><Close fontSize="small" /></IconButton>}
          sx={{ borderRadius: 2, maxWidth: 560, alignItems: "center" }}
        >
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SaleEngine;
